/**
 * OAuth Store
 * Framework-agnostic observable store for OAuth connections
 * Uses observer pattern for reactive updates
 */

import type { OAuthConnection, OAuthStateListener } from './types';

export class OAuthStore {
  private connections: OAuthConnection[] = [];
  private listeners: Set<OAuthStateListener> = new Set();
  private loading: boolean = false;
  private error: string | null = null;

  /**
   * Subscribe to connection state changes
   *
   * @param listener - Callback function invoked on state changes
   * @returns Unsubscribe function
   */
  subscribe(listener: OAuthStateListener): () => void {
    this.listeners.add(listener);

    // Immediately invoke with current state
    listener(this.connections);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Update connections and notify all listeners
   */
  setConnections(connections: OAuthConnection[]): void {
    this.connections = connections;
    this.notify();
  }

  /**
   * Get current connections (non-reactive)
   */
  getConnections(): OAuthConnection[] {
    return [...this.connections];
  }

  /**
   * Get connection for specific platform
   */
  getConnection(platform: string): OAuthConnection | null {
    return this.connections.find(c => c.platform === platform) || null;
  }

  /**
   * Add or update a single connection
   */
  upsertConnection(connection: OAuthConnection): void {
    const index = this.connections.findIndex(c => c.id === connection.id);

    if (index >= 0) {
      this.connections[index] = connection;
    } else {
      this.connections.push(connection);
    }

    this.notify();
  }

  /**
   * Remove a connection
   */
  removeConnection(connectionId: string): void {
    this.connections = this.connections.filter(c => c.id !== connectionId);
    this.notify();
  }

  /**
   * Set loading state
   */
  setLoading(loading: boolean): void {
    this.loading = loading;
    this.notify();
  }

  /**
   * Get loading state
   */
  isLoading(): boolean {
    return this.loading;
  }

  /**
   * Set error state
   */
  setError(error: string | null): void {
    this.error = error;
    this.notify();
  }

  /**
   * Get error state
   */
  getError(): string | null {
    return this.error;
  }

  /**
   * Clear all connections
   */
  clear(): void {
    this.connections = [];
    this.loading = false;
    this.error = null;
    this.notify();
  }

  /**
   * Notify all listeners of state change
   */
  private notify(): void {
    this.listeners.forEach(listener => {
      try {
        listener(this.connections);
      } catch (error) {
        console.error('Error in OAuth store listener:', error);
      }
    });
  }
}
